import React, { useCallback } from "react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";

import { css } from "@emotion/react";

import { AutoHeaderSpacer } from "../_layout-helpers/auto-header-spacer";
import { optToMaybe } from "../../utils/fp/small-fp-utils";
import { head } from "fp-ts/es6/Array";
import { ComponentRenderer } from "../_layout-helpers/component-renderer";
import { Spacer } from "../../components/_shared-components/arrangement/Space";
import { HeaderComponent } from "../../components/HeaderComponent/HeaderComponent";
import { FooterComponent } from "../../components/FooterComponent/FooterComponent";
import { getIsoCodeWithIntFallback } from "@swo/shared";
import { ExtractSwarovskiPageData, ProductComparisonPageData, ProductComparisonResponseData } from "@swo/storefront";
import { ProductComparisonPageComponent } from "./product-comparison-page-component";
import { option } from "fp-ts";
import { Option } from "fp-ts/es6/Option";
import { t2 } from "../../domain/translations/translations";

export const ProductComparisonPageLayout: ReactTFC<ExtractSwarovskiPageData<ProductComparisonPageData>> = props => {
  const countryIsoCode = getIsoCodeWithIntFallback(props.locale.country);
  const gt = t2(props.generalTranslations);

  const fetcherByProductKeys = useCallback(
    (productKeys: Array<string>) => {
      return fetcher(props.page.localeCode, props.segment.key, productKeys);
    },
    [props.page.localeCode, props.page.segmentId],
  );

  return (
    <React.Fragment>
      <HeaderComponent header={props.data.header} version={"normal"} />
      <AutoHeaderSpacer
        componentAfterHeaderTypeName={optToMaybe(
          head(props.data.components.map(c => (typeof c === "string" ? undefined : c.__typename))),
        )}
      />

      <ProductComparisonPageComponent
        countryIsoCode={countryIsoCode}
        products={props.data.products}
        fetcherByProductKeys={fetcherByProductKeys}
        translations={props.data.translations}
        fromPriceLabelTranslation={gt("fromPriceLabel")}
        localeForNumberFormat={props.locale}
      />

      <ComponentRenderer components={props.data.components} localeForNumberFormat={props.locale} translations={gt} />
      <Spacer size={"md"} />
      <FooterComponent {...props.data.footer} />
    </React.Fragment>
  );
};

function fetcher(
  localeCode: string,
  segmentKey: string,
  productKeys: Array<string>,
): Promise<Option<ProductComparisonResponseData>> {
  let urlParams = new URLSearchParams(productKeys.map(productKey => ["product", productKey]));
  urlParams.append("localeCode", localeCode);
  urlParams.append("segmentKey", segmentKey);

  return fetch(`/storefront/product-comparison?${urlParams.toString()}`)
    .then(res => res.json())
    .then(json => {
      if (json.error) {
        return option.none;
      } else {
        return option.some(json as ProductComparisonResponseData);
      }
    })
    .catch(() => {
      return option.none;
    });
}
